import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  IconButton,
  LinearProgress,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "common/locales";
import {
  fetchAllProgrammeActions,
  fetchSubmittedActionsByEmail,
  selectAllProgrammeActions,
  selectSubmittedActionEntryById,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getFormattedCategoryName } from "./ActionList";

interface ActionDetailsParams {
  id: string;
}

export default function ActionDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams<ActionDetailsParams>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const actionPlanEntry = useAppSelector((state) =>
    selectSubmittedActionEntryById(state, id)
  );

  const user = useAppSelector((state) => state.user.details);
  const allProgrammeActions = useAppSelector(selectAllProgrammeActions);

  // Fetch action plan entries if not loaded
  useEffect(() => {
    if (user?.mail && !actionPlanEntry) {
      const workaroundFunc = async () => {
        await dispatch(fetchSubmittedActionsByEmail(user.mail));
      };
      void workaroundFunc();
    }
  }, [dispatch, user?.mail, actionPlanEntry]);

  // Fetch programme actions if not loaded
  useEffect(() => {
    if (allProgrammeActions.length === 0) {
      const workaroundFunc = async () => {
        await dispatch(fetchAllProgrammeActions());
      };
      void workaroundFunc();
    }
  }, [dispatch, allProgrammeActions.length]);

  const loading = useAppSelector(
    (state) => state.submittedActions.loading === "pending"
  );

  // Find the programme - for now, default to "Lidl Sustainable Beef Programme" or first programme
  // In the future, this could come from the action plan entry or route params
  const programmeActions =
    allProgrammeActions.find(
      (p) =>
        p.programmeName.toLowerCase().includes("lidl") ||
        p.programmeName.toLowerCase().includes("sustainable beef")
    ) || allProgrammeActions[0];

  const handleBack = () => {
    history.goBack();
  };

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const openPopover = Boolean(popoverAnchor);
  if (loading || !actionPlanEntry) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "white",
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Banner Header - same as ProgrammeDetailsWithBanner */}
      <Box
        sx={{
          height: { xs: "96px", sm: "120px" },
          borderRadius: "8px",
          mb: 3,
          ...(programmeActions?.guid === "6vczLKixKSwPkIFP3JkkGD"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/lidl-sustainable-beef-banner.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#395499",
              }
            : programmeActions?.guid === "3UqQUHVbKY5CMBthVL5Ltq"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/Pattemores.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "repeat",
                backgroundColor: "#ffffffff",
              }
            : programmeActions?.guid === "5OPgsM2LDKgFHqhLO4Kzpv"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/County-Milk-2.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "repeat",
                backgroundColor: "#ffffffff",
              }
            : {
                backgroundColor: "#1C4321",
              }),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {programmeActions &&
          programmeActions.guid !== "6vczLKixKSwPkIFP3JkkGD" &&
          programmeActions.guid !== "3UqQUHVbKY5CMBthVL5Ltq" &&
          programmeActions.guid !== "5OPgsM2LDKgFHqhLO4Kzpv" && (
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 600,
                textAlign: "center",
                px: 2,
              }}
            >
              {programmeActions.programmeName}
            </Typography>
          )}
      </Box>

      {/* Main Content */}
      {/* Top Bar with Back Button and Programme Name */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ border: "1px solid #e4e4e7" }}>
            <ArrowBackIcon />
          </IconButton>
          {programmeActions && (
            <Box
              sx={{
                border: "1px solid #e4e4e7",
                borderRadius: "9999px",
                px: 1.25,
                py: 0.25,
              }}
            >
              <Typography variant="body2">{"Farm Action Plan"}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Action Details and Documentation Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Left Section - Action Details */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "0 0 379px" },
            border: "1px solid #e4e4e7",
            borderRadius: "8px",
            p: 3,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                mb: 0.75,
                color: "#09090b",
              }}
            >
              Action Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#71717a",
                fontSize: "14px",
              }}
            >
              Details about the action completed by the farmer
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Deadline 
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: "#18181b",
                  fontSize: "14px",
                }}
              >
                Deadline
              </Typography>
              <Box
                sx={{
                  bgcolor: "#eaeaea",
                  border: "1px solid #e4e4e7",
                  borderRadius: "6px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "#18181b",
                    fontSize: "14px",
                  }}
                >
                  {actionPlanEntry.deadline || "N/A"}
                </Typography>
              </Box>
            </Box>
*/}
            {/* Category */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: "#18181b",
                  fontSize: "14px",
                }}
              >
                Category
              </Typography>
              <Box
                sx={{
                  bgcolor: "#eaeaea",
                  border: "1px solid #e4e4e7",
                  borderRadius: "6px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "#18181b",
                    fontSize: "14px",
                  }}
                >
                  {getFormattedCategoryName(actionPlanEntry.categoryId) ||
                    actionPlanEntry.category ||
                    "N/A"}
                </Typography>
              </Box>
            </Box>

            {/* Activity */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: "#18181b",
                  fontSize: "14px",
                }}
              >
                Activity
              </Typography>
              <Box
                sx={{
                  bgcolor: "#eaeaea",
                  border: "1px solid #e4e4e7",
                  borderRadius: "6px",
                  minHeight: "80px",
                  p: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#18181b",
                    fontSize: "14px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {actionPlanEntry.action || "N/A"}
                </Typography>
              </Box>
            </Box>

            {/* Your Notes */}
            {actionPlanEntry.submittedDetails && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#18181b",
                      fontSize: "14px",
                    }}
                  >
                    Your notes
                  </Typography>
                  {actionPlanEntry.detailQuestion && (
                    <>
                      {isMobile ? (
                        <>
                          <IconButton
                            onClick={handleInfoClick}
                            size="small"
                            sx={{
                              padding: 0,
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                          >
                            <InfoIcon
                              sx={{
                                fontSize: "16px",
                                color: "#71717a",
                                cursor: "pointer",
                              }}
                            />
                          </IconButton>
                          <Popover
                            open={openPopover}
                            anchorEl={popoverAnchor}
                            onClose={handlePopoverClose}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                            slotProps={{
                              paper: {
                                sx: {
                                  mb: "80px", // Ensure popover is above bottom navigation
                                },
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                p: 1.5,
                                maxWidth: "280px",
                                fontSize: "14px",
                              }}
                            >
                              This is your answer to the question:{" "}
                              <Typography
                                component="span"
                                sx={{ fontStyle: "italic", fontSize: "14px" }}
                              >
                                {actionPlanEntry.detailQuestion}
                              </Typography>
                            </Typography>
                          </Popover>
                        </>
                      ) : (
                        <Tooltip
                          title={
                            <Typography component="span">
                              This is your answer to the question:{" "}
                              <Typography
                                component="span"
                                sx={{ fontStyle: "italic", fontSize: "14px" }}
                              >
                                {actionPlanEntry.detailQuestion}
                              </Typography>
                            </Typography>
                          }
                          arrow
                        >
                          <InfoIcon
                            sx={{
                              fontSize: "16px",
                              color: "#71717a",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    bgcolor: "#eaeaea",
                    border: "1px solid #e4e4e7",
                    borderRadius: "6px",
                    minHeight: "80px",
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#18181b",
                      fontSize: "14px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {actionPlanEntry.submittedDetails}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Section - Documentation (empty for now) */}
        <Box
          sx={{
            flex: "1 1 auto",
            border: "1px solid #e4e4e7",
            borderRadius: "8px",
            p: 3,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AssignmentTurnedInIcon sx={{ color: "#16a34a", fontSize: 28 }} />
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 600,
                  mb: 0.75,
                  color: "#09090b",
                }}
              >
                Documentation
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#71717a",
                fontSize: "14px",
                mt: 1,
              }}
            >
              This is where you&apos;ll be able to upload photos and other
              evidence showing how you&apos;ve put your actions into practice.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#71717a",
                fontSize: "14px",
                mt: 0.5,
              }}
            >
              We&apos;re still building this feature, so for now you don&apos;t
              need to upload anything here.
            </Typography>
          </Box>
          {/* Content will be added later */}
        </Box>
      </Box>
    </Box>
  );
}
